import React from 'react'
import { Classes, Collapse, Colors, Icon, IconName } from '@blueprintjs/core'
import { Box, quarks, Stack, Text } from 'ui-components'
import { CommentIO, CommentT, TaskT } from 'ui-io/task-io/task-t'
import styled from 'styled-components'
import { useQuery } from 'react-query'
import { userQuery } from 'ui-io/queries/users/user-query'

import Avatar from './avatar'
import Attachment from './attachment'
import { ActiveAttachmentT } from '.'
import normalizeUserQueryData from '../../../utils/normalize-user-query-data'
import { AssetReferenceT, UrlExternalReferenceT } from 'ui-io/task-io'

type DiscussionPanelProps = {
  task: TaskT
  handleToggleIsAttachmentOpen: (nextAttachment: ActiveAttachmentT) => void
  onDelete: (attachmentId: string | undefined) => void
}

type CollapseIdT = 'isParticipantsOpen' | 'isAttachmentsOpen'

const DiscussionPanel = (props: DiscussionPanelProps) => {
  const { task, handleToggleIsAttachmentOpen, onDelete } = props
  const [collapsable, setCollapsable] = React.useState({
    isParticipantsOpen: true,
    isAttachmentsOpen: true,
  })
  const participants = task.uniqDiscussionParticipants

  const handleOnCollapse = (id: CollapseIdT) => {
    setCollapsable((prev) => {
      return {
        ...prev,
        [id]: !prev[id],
      }
    })
  }
	const totalFiles = task.comments.reduce((total: number, note: CommentT) => {
		return total + note.attachments.length;
	}, 0)

  return (
    <Panel>
      <Stack spacing={quarks.margins.small}>
        <CollapsableItem
          icon="person"
          title="Participants"
          id="isParticipantsOpen"
          subTitle={`${participants.length} ${
            participants.length <= 1 ? 'person' : 'people'
          }`}
          isOpen={collapsable.isParticipantsOpen}
          handleOnCollapse={handleOnCollapse}
        >
          {participants.map((username: string) => {
            return <DiscussionParticipant key={username} username={username} />
          })}
        </CollapsableItem>
        <CollapsableItem
          icon="paperclip"
          title="Attachments"
          id="isAttachmentsOpen"
          subTitle={`${totalFiles} ${
            totalFiles <= 1 ? 'file' : 'files'
          }`}
          isOpen={collapsable.isAttachmentsOpen}
          handleOnCollapse={handleOnCollapse}
        >
          {task.comments.map((note:CommentT) => {
            const { attachments, username, timestamp } = note
            return attachments.length>0 && attachments.map((attachment:UrlExternalReferenceT, index:number) => {
              return (
                <AttachmentWrapper key={index}>
                  <Attachment
                    attachment={attachment}
                    isUsersComment={true}
                    handleToggleIsAttachmentOpen={handleToggleIsAttachmentOpen}
                    onDelete={onDelete}
                    uploadTimeStamp={timestamp || -1}
                    uploadedUser={username || ''}
                  />
                </AttachmentWrapper>
              )
            })
          })}
        </CollapsableItem>
      </Stack>
    </Panel>
  )
}

type CollapsableItemT = {
  isOpen: boolean
  handleOnCollapse: (key: CollapseIdT) => void
  title: string
  subTitle: string
  children: React.ReactNode
  icon: IconName
  id: CollapseIdT
}

const CollapsableItem = (props: CollapsableItemT) => {
  const { isOpen, handleOnCollapse, icon, title, subTitle, id, children } =
    props

  return (
    <div>
      <AssetFolder onClick={() => handleOnCollapse(id)} isOpen={isOpen}>
        <AssetIconWrapper>
          <Icon icon={icon} />
        </AssetIconWrapper>
        <div style={{ flex: '1' }}>
          <Text caption mb="xsmall">
            {title}
          </Text>
          <Text small muted>
            {subTitle}
          </Text>
        </div>
        <div style={{ alignSelf: 'center' }}>
          <Icon icon={isOpen ? 'caret-down' : 'caret-right'} iconSize={18} />
        </div>
      </AssetFolder>
      <Collapse isOpen={isOpen}>
        <Stack style={{ margin: '12px 0' }} spacing={quarks.margins.small}>
          {children}
        </Stack>
      </Collapse>
    </div>
  )
}

const DiscussionParticipant = ({ username }: { username: string }) => {
  const { data, isLoading, isError } = useQuery(userQuery(username))
  const { email, displayName } = normalizeUserQueryData({
    data,
    username,
  })

  return (
    <div style={{ display: 'flex', marginLeft: 40 }}>
      <Avatar
        email={email}
        displayName={displayName}
        isCurrentUser={false}
        isLoading={isLoading}
      />
      <Box flexDir="col" justify="center">
        <Text
          small
          className={isLoading ? Classes.SKELETON : ''}
          mb="xxsmall"
          style={isError ? { fontStyle: 'italic' } : {}}
        >
          {isError ? username : displayName}
        </Text>
        {!isError && (
          <a
            className={isLoading ? Classes.SKELETON : ''}
            style={{ fontSize: 12 }}
            href={`mailto:${email}`}
          >
            {email}
          </a>
        )}
      </Box>
    </div>
  )
}

const AttachmentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  margin-left: 40px;
`

const Panel = styled.div`
  width: calc(100% - 4px);
  height: calc(100% - 10px);
  background-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY2 : Colors.LIGHT_GRAY4
  }};
  position: absolute;
  display: flex;
  flex-direction: column;
  left: 2px;
	padding:20px;
  overflow: auto;
  z-index: 4;
`

const AssetFolder = styled.div<{ isOpen: boolean }>`
  display: flex;
  padding: 8px;
  border-radius: 4px;

  &:hover {
    background-color: ${(props) => {
      return props.theme.darkMode ? Colors.DARK_GRAY1 : Colors.LIGHT_GRAY3
    }};
    cursor: pointer;
  }
`

const AssetIconWrapper = styled.div`
  padding: 6px;
  border-radius: 6px;
  background-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY2 : Colors.WHITE
  }};
  color: ${(props) => {
    return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY3
  }};
  margin-right: 6px;
`

export default DiscussionPanel
